import { type LoadFileDelimiters } from '../../../../../../../../../../backend/src/integrations/relativity/load-file-import/concordance-dat-parser';
import {
  LoadFileEncoding,
  type ParseHeaderSampleResponse,
} from '../../../../../../../../../../backend/src/integrations/relativity/load-file-import/load-file-import-types';
import {
  COLUMN_DELIMITER_OPTIONS,
  type DelimiterOption,
  ENCODING_OPTIONS,
  MULTI_VALUE_OPTIONS,
  NEWLINE_OPTIONS,
  QUOTE_OPTIONS,
  withDetectedOption,
} from '../helpers';
import { Icon } from '@/new/components/icon';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { cn } from '@/new/lib/utils';

interface LoadFileSettingsStepProps {
  parseResult: ParseHeaderSampleResponse;
  delimiters: LoadFileDelimiters;
  /** True while the preview is being re-parsed after a settings change. */
  isParsing: boolean;
  onDelimitersChange: (partial: Partial<LoadFileDelimiters>) => void;
}

export function LoadFileSettingsStep({
  parseResult,
  delimiters,
  isParsing,
  onDelimitersChange,
}: LoadFileSettingsStepProps) {
  return (
    <div className="grid min-h-0 flex-1 grid-cols-2 gap-6 pb-1">
      {/* Delimiters and encoding */}
      <div className="flex h-full min-h-0 flex-col">
        <h2 className="mb-3 text-base">Delimiters and Encoding Settings</h2>

        <div className="my-4 flex flex-col gap-5">
          <div>
            <Label>File Encoding</Label>
            <Select
              value={delimiters.encoding}
              onValueChange={(v) => onDelimitersChange({ encoding: v as LoadFileEncoding })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENCODING_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DelimiterSelect
            label="Column"
            options={COLUMN_DELIMITER_OPTIONS}
            value={delimiters.field}
            onChange={(field) => onDelimitersChange({ field })}
          />
          <DelimiterSelect
            label="Quote"
            options={QUOTE_OPTIONS}
            value={delimiters.quote}
            onChange={(quote) => onDelimitersChange({ quote })}
          />
          <DelimiterSelect
            label="Newline for Long Text"
            hint="Character that stands in for a line break inside long text fields."
            options={NEWLINE_OPTIONS}
            value={delimiters.newline}
            onChange={(newline) => onDelimitersChange({ newline })}
          />
          <DelimiterSelect
            label="Multi-Value"
            hint="Separator between the values of a multi-value (array) field."
            options={MULTI_VALUE_OPTIONS}
            value={delimiters.multiValue}
            onChange={(multiValue) => onDelimitersChange({ multiValue })}
          />
        </div>
      </div>

      {/* Live header preview */}
      <div className="flex h-full min-h-0 flex-col">
        <div className="mb-3 space-y-0.5">
          <h2 className="text-base">File Column Headers Preview</h2>
          <p className="text-gray-500">These should be the names of the metadata fields.</p>
        </div>

        {parseResult.headers.length === 0 ? (
          <div className="max-w-md text-sm text-gray-400">
            No columns parsed. Adjust the delimiters and encoding until you have the correct headers.
          </div>
        ) : (
          <div className="relative min-h-0 max-w-md flex-1 overflow-y-auto rounded-lg border">
            <div className={cn('transition-opacity', isParsing && 'opacity-40')}>
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-gray-50">
                  <TableRow>
                    <TableHead className="h-auto w-0 px-3 py-2 shadow-[inset_-1px_0_0_0_#e5e7eb]" />
                    <TableHead className="h-auto px-3 py-2">
                      Column Name <span className="font-medium">({parseResult.headers.length})</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parseResult.headers.map((header, index) => (
                    <TableRow key={`${header}-${index}`}>
                      <TableCell className="text w-0 border-r bg-gray-50 px-3 py-2 text-center text-xs text-gray-400 tabular-nums">
                        {index + 1}
                      </TableCell>
                      <TableCell className="max-w-0 truncate px-3 py-2 text-gray-600" title={header}>
                        {header}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {isParsing && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon className="lucide--loader animate-spin text-gray-500 ease-in-out" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** A labelled single-character delimiter Select, always including the current value. */
function DelimiterSelect({
  label,
  hint,
  options,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  options: DelimiterOption[];
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="mb-0">{label}</Label>
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {withDetectedOption(options, value).map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}
